// Cuadre de cuentas de control. Contabilidad → Libros y Auxiliares.
//
// El primer control de todo cierre: ¿la cartera por cobrar cuadra con la cuenta
// Clientes del mayor? Hasta ahora eso se hacía exportando dos reportes y
// restándolos en Excel, y por eso casi nunca se hacía.
//
// La pantalla distingue tres cosas, y la distinción es el punto:
//   · CAUSAS   — aritmética demostrable: se restan de la diferencia.
//   · SEÑALES  — no se restan, pero dicen dónde mirar. Un pago bancario sin
//                factura enlazada puede estar compensado por un abono suelto,
//                así que contarlo como explicación sería mentir.
//   · SIN EXPLICAR — lo que queda, con ese nombre. Repartir la diferencia con
//                heurísticas hasta que cuadre en pantalla daría una falsa
//                tranquilidad, que es peor que no tener la pantalla.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Scale } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { exportarCSV, field, hoy, rpc, usd } from './shared';

interface Causa { clave: string; etiqueta: string; documentos: number; importe: number | null }
interface Cuenta {
  concepto: string; etiqueta: string; cuenta: string;
  submayor: number; mayor: number; diferencia: number;
  causas: Causa[]; senales: Causa[]; sin_explicar: number | null;
}
interface Detalle { fecha: string; documento: string | null; tercero: string | null; importe: number; glosa?: string }

const CSV_HEAD = ['Fecha', 'Documento', 'Tercero', 'Importe'];
const cuadra = (c: Cuenta) => Math.abs(Number(c.diferencia)) < 0.01;

function DetalleCausa({ concepto, causa, hasta, etiqueta }: { concepto: string; causa: string; hasta: string; etiqueta: string }) {
  const { data = [], isFetching } = useQuery<Detalle[]>({
    queryKey: ['cont-cuadre-det', concepto, causa, hasta],
    queryFn: () => rpc<Detalle[]>('cont_cuadre_detalle', { _concepto: concepto, _causa: causa, _hasta: hasta }),
  });
  if (isFetching) return <div className="py-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-primary" /></div>;
  if (!data.length) return <p className="py-3 text-sm text-muted-foreground">Sin documentos que listar.</p>;
  return (
    <div className="mt-2">
      <div className="flex justify-end mb-1">
        <button className="no-print text-xs text-primary hover:underline"
          onClick={() => exportarCSV(`cuadre-${concepto}-${causa}`, CSV_HEAD,
            data.map((d) => [d.fecha, d.documento ?? '', d.tercero ?? '', d.importe]))}>
          Exportar CSV
        </button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-xs text-muted-foreground">
          <th className="py-1.5 pr-3">Fecha</th><th className="py-1.5 pr-3">Documento</th>
          <th className="py-1.5 pr-3">{etiqueta.includes('Asientos') ? 'Origen' : 'Tercero'}</th>
          <th className="py-1.5 pr-3 text-right">Importe</th>
        </tr></thead>
        <tbody>
          {data.map((d, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="py-1.5 pr-3 whitespace-nowrap">{d.fecha}</td>
              <td className="py-1.5 pr-3 font-mono text-xs">{d.documento ?? '—'}</td>
              <td className="py-1.5 pr-3">{d.tercero ?? '—'}
                {d.glosa && <div className="text-xs text-muted-foreground">{d.glosa}</div>}</td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{usd(d.importe)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length >= 300 && (
        <p className="mt-1 text-xs text-muted-foreground">Se muestran los primeros 300. Exporta el CSV para verlos todos.</p>
      )}
    </div>
  );
}

function FilaCuenta({ c, hasta }: { c: Cuenta; hasta: string }) {
  const [abierta, setAbierta] = useState<string | null>(null);
  const ok = cuadra(c);
  const causasConAlgo = c.causas.filter((x) => x.documentos > 0);
  const senales = (c.senales ?? []).filter((x) => x.documentos > 0);

  return (
    <Card className={ok ? '' : 'border-amber-500/40'}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle className="text-base">{c.etiqueta}</CardTitle>
          <span className="text-xs text-muted-foreground font-mono">{c.cuenta}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><div className="text-xs text-muted-foreground">Submayor</div>
            <div className="font-mono tabular-nums">{usd(c.submayor)}</div></div>
          <div><div className="text-xs text-muted-foreground">Mayor</div>
            <div className="font-mono tabular-nums">{usd(c.mayor)}</div></div>
          <div><div className="text-xs text-muted-foreground">Diferencia</div>
            <div className={`font-mono tabular-nums font-semibold ${ok ? 'text-green-600' : 'text-amber-600'}`}>
              {usd(c.diferencia)}</div></div>
          <div><div className="text-xs text-muted-foreground">Sin explicar</div>
            <div className={`font-mono tabular-nums font-semibold ${
              c.sin_explicar === null ? 'text-muted-foreground'
              : Math.abs(Number(c.sin_explicar)) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
              {c.sin_explicar === null ? '—' : usd(c.sin_explicar)}</div></div>
        </div>

        {ok && <p className="text-sm text-green-600">Cuadra.</p>}

        {causasConAlgo.length > 0 && (
          <div className="border-t pt-2">
            <p className="text-xs font-medium text-muted-foreground mb-1">De qué está hecha la diferencia</p>
            {causasConAlgo.map((x) => (
              <div key={x.clave}>
                <button onClick={() => setAbierta(abierta === x.clave ? null : x.clave)}
                  className="flex w-full items-center gap-2 rounded px-1 py-1.5 text-sm text-left hover:bg-muted">
                  {abierta === x.clave ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                  <span className="flex-1">{x.etiqueta}</span>
                  <span className="text-muted-foreground text-xs">{x.documentos} doc.</span>
                  <span className="font-mono tabular-nums w-28 text-right">
                    {x.importe === null ? '—' : usd(x.importe)}</span>
                </button>
                {abierta === x.clave && (
                  <div className="pl-6 pb-2 overflow-x-auto">
                    <DetalleCausa concepto={c.concepto} causa={x.clave} hasta={hasta} etiqueta={x.etiqueta} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {senales.length > 0 && (
          <div className="border-t pt-2">
            <p className="flex items-center gap-1.5 text-xs font-medium text-amber-600 mb-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Señales — no restan de la diferencia, pero es donde mirar
            </p>
            {senales.map((x) => (
              <div key={x.clave} className="flex items-center gap-2 px-1 py-1.5 text-sm">
                <span className="flex-1">{x.etiqueta}</span>
                <span className="text-muted-foreground text-xs">{x.documentos} doc.</span>
                <span className="font-mono tabular-nums w-28 text-right">{usd(x.importe ?? 0)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function CuadreControl() {
  const [hasta, setHasta] = useState(hoy);
  const { data, isFetching } = useQuery<{ fecha: string; cuentas: Cuenta[] }>({
    queryKey: ['cont-cuadre', hasta],
    queryFn: () => rpc('cont_cuadre', { _hasta: hasta }),
  });
  const cuentas = data?.cuentas ?? [];
  const descuadradas = cuentas.filter((c) => !cuadra(c)).length;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Cuadre de Cuentas de Control</h1>
        <PrintButton />
      </div>
      <p className="text-sm text-muted-foreground">
        Cada submayor contra su cuenta del mayor. Lo que la diferencia tenga de demostrable se resta y se puede
        abrir documento a documento; lo que no, queda como «sin explicar» con ese nombre.
      </p>

      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Al
          <input type="date" className={`${field} mt-1`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
      </CardContent></Card>

      {!isFetching && cuentas.length > 0 && (
        <p className={`flex items-center gap-2 text-sm ${descuadradas ? 'text-amber-600' : 'text-green-600'}`}>
          <Scale className="w-4 h-4" />
          {descuadradas === 0
            ? `Las ${cuentas.length} cuentas de control cuadran.`
            : `${descuadradas} de ${cuentas.length} cuentas de control no cuadran.`}
        </p>
      )}

      <Imprimible titulo="Cuadre de Cuentas de Control" subtitulo={`Al ${hasta}`}>
        <div className="space-y-3">
          {cuentas.map((c) => <FilaCuenta key={c.concepto} c={c} hasta={hasta} />)}
          {!cuentas.length && !isFetching && (
            <Card><CardContent className="py-10 text-center text-muted-foreground">
              No hay cuentas de control mapeadas. Asígnalas en Configuración Contable.
            </CardContent></Card>
          )}
        </div>
      </Imprimible>
    </div>
  );
}
